/**
 * 器のスキン（県ゆかりの焼き物・漆器）。
 *
 * 解放条件はその県で銅メダル（Lv10）。「その県を遊ぶ理由」をもう1つ作る。
 * 金継ぎだけは金メダル5県（腕前のごほうび）。
 * 見た目だけで、器の形・大きさ・当たり判定は変えない（難易度が全員同じになるように）。
 */

export type BowlPattern = 'none' | 'karakusa' | 'stripes' | 'flecks' | 'drip' | 'kintsugi';

export interface BowlSkin {
  id: string;
  name: string;
  /** 由来の県（regionId）。既定の器は null。 */
  regionId: string | null;
  /** 解放条件の説明。 */
  unlock: string;
  body: number;
  /** 胴の光（左側）の強さ。 */
  highlight: number;
  foot: number;
  /** 器の内側に重ねる色と濃さ（背景を透かす）。 */
  inner: number;
  innerAlpha: number;
  /** 縁の線。null ならステージのテーマ色。 */
  rim: number | null;
  pattern: BowlPattern;
  patternColor: number;
  /** アプリ版の買い切りのお礼。Web 版では一覧に出さない（Web には課金がないため）。 */
  appOnly?: boolean;
}

export const DEFAULT_BOWL_SKIN_ID = 'yatai';

export const BOWL_SKINS: readonly BowlSkin[] = [
  {
    id: 'yatai',
    name: '屋台のどんぶり',
    regionId: null,
    unlock: 'はじめから',
    body: 0x2b1a20,
    highlight: 0.07,
    foot: 0x1d1216,
    inner: 0x0e0910,
    innerAlpha: 0.24,
    rim: null,
    pattern: 'none',
    patternColor: 0,
  },
  {
    id: 'arita',
    name: '有田焼（白磁に藍）',
    regionId: 'saga',
    unlock: '佐賀県で銅メダル',
    body: 0xf1eee6,
    highlight: 0.18,
    foot: 0xd8d2c4,
    inner: 0xf6f3ec,
    innerAlpha: 0.12,
    rim: 0x23408e,
    pattern: 'karakusa',
    patternColor: 0x23408e,
  },
  {
    id: 'hasami',
    name: '波佐見焼（藍の線）',
    regionId: 'nagasaki',
    unlock: '長崎県で銅メダル',
    body: 0xe9ecef,
    highlight: 0.14,
    foot: 0xc7ccd2,
    inner: 0xe9ecef,
    innerAlpha: 0.1,
    rim: 0x2d4f7a,
    pattern: 'stripes',
    patternColor: 0x2d4f7a,
  },
  {
    id: 'wajima',
    name: '輪島塗（黒漆に金）',
    regionId: 'ishikawa',
    unlock: '石川県で銅メダル',
    body: 0x121012,
    highlight: 0.1,
    foot: 0x0a0809,
    inner: 0x0a0809,
    innerAlpha: 0.3,
    rim: 0xd8b04a,
    pattern: 'flecks',
    patternColor: 0xd8b04a,
  },
  {
    id: 'ryukyu',
    name: '琉球漆器（朱）',
    regionId: 'okinawa',
    unlock: '沖縄県で銅メダル',
    body: 0xa3231f,
    highlight: 0.12,
    foot: 0x6d1512,
    inner: 0x2a0a08,
    innerAlpha: 0.22,
    rim: 0xe8c25a,
    pattern: 'none',
    patternColor: 0,
  },
  {
    id: 'mashiko',
    name: '益子焼（柿釉）',
    regionId: 'tochigi',
    unlock: '栃木県で銅メダル',
    body: 0x7a3a1a,
    highlight: 0.1,
    foot: 0x5a2a12,
    inner: 0x2a150a,
    innerAlpha: 0.24,
    rim: 0xc9985c,
    pattern: 'drip',
    patternColor: 0x2b1a12,
  },
  {
    id: 'shigaraki',
    name: '信楽焼（土の色）',
    regionId: 'shiga',
    unlock: '滋賀県で銅メダル',
    body: 0xb98a5a,
    highlight: 0.14,
    foot: 0x94693f,
    inner: 0x3a2616,
    innerAlpha: 0.2,
    rim: 0x6b4526,
    pattern: 'flecks',
    patternColor: 0x6b4526,
  },
  {
    id: 'kintsugi',
    name: '金継ぎ',
    regionId: null,
    unlock: '金メダルを5県で',
    body: 0x1c1a1b,
    highlight: 0.08,
    foot: 0x121011,
    inner: 0x0e0c0d,
    innerAlpha: 0.26,
    rim: 0xf0c75e,
    pattern: 'kintsugi',
    patternColor: 0xf0c75e,
  },
  {
    id: 'shu-urushi',
    name: '朱の漆椀',
    regionId: null,
    unlock: '買い切り（広告なし）のお礼',
    body: 0x9e2a22,
    highlight: 0.12,
    foot: 0x1c1212,
    inner: 0x1a0d0b,
    innerAlpha: 0.24,
    rim: 0x1c1212,
    pattern: 'none',
    patternColor: 0x1c1212,
    appOnly: true,
  },
];

export function bowlSkinById(id: string | undefined): BowlSkin {
  return BOWL_SKINS.find((s) => s.id === id) ?? BOWL_SKINS[0];
}
